import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";

import { IPatientDme } from "./../../../../../../resources-vue/vue-interfaces/i-get-dme";
import { formatDate, formatDateInput } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import { ToastrService } from "../../../../../../services/toastr.service";

import DatePicker from "../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import TablePrompt from "../../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";
import CommentInput from "./../../../../../../resources-vue/vue-custom-elements/CommentInput/CommentInput.vue";

export default defineComponent({
    components: {
        DatePicker,
        TablePrompt,
        CommentInput
    },
    props: {
        dme: {
            type: Object as PropType<IPatientDme>,
            default: {}
        },
        updateDme: { type: Function },
        cancelNewDme: { type: Function },
        deleteDmeFromList: { type: Function },
        isNewDme: { type: Boolean, default: false },
        isEditAllowed: { type: Boolean, default: false },
        columns: { type: Number, default: 6 }
    },
    computed: {
        isDmeRelatedBoolean() {
            return typeof this.dme.isRelated === "boolean";
        }
    },
    watch: {
        dme: {
            handler() {
                if (this.isNewDme) {
                    this.isEditRequested = true;
                }
            },
            immediate: true
        }
    },
    validations() {
        return {
            dme: {
                startDate: { required }
            }
        };
    },
    data() {
        return {
            isEditRequested: false,
            isDeleteRequested: false,
            cacheDme: null,
            _toastrService: null,
            formatDate,
            formatDateInput,
            v$: useVuelidate()
        };
    },
    created() {
        this._toastrService = Container.instance.get(ToastrService);
    },
    methods: {
        onDateChange(date: any, name: string) {
            if (name === "dmeStartDate") this.dme.startDate = date;
            if (name === "dmeEndDate") this.dme.endDate = date;
        },
        editDme() {
            this.isEditRequested = true;
            this.cacheDme = Object.assign({}, this.dme);
        },
        requestDeleteDme() {
            this.isDeleteRequested = true;
        },
        async saveEdit() {
            const validated = await this.v$.$validate();
            if (!this.dme.startDate) {
                this._toastrService.error({
                    title: `Start Date Required`,
                    message: `The Start Date is required for a DME. Please update and try again.`
                });
                return;
            } else if (moment(this.dme.startDate) > moment(this.dme.endDate)) {
                this._toastrService.error({
                    title: `Error`,
                    message: `The End Date cannot be before Start Date. Please update and try again.`
                });
                return;
            }
            if (!validated) {
                return;
            }
            this.updateDme(this.dme, this.cacheDme);
            this.isEditRequested = false;
        },
        cancelEdit() {
            if (this.isNewDme) {
                this.cancelNewDme();
            } else {
                this.updateDme(JSON.parse(JSON.stringify(this.cacheDme)), this.dme);
                this.isEditRequested = false;
            }
        },
        deleteDme() {
            this.deleteDmeFromList(this.dme);
            this.isDeleteRequested = false;
        },
        cancelDelete() {
            this.isDeleteRequested = false;
        },
        resultUpdatedAppended(value: string) {
            this.dme.notRelatedComments += value;
        },
        resultUpdated(value: string) {
            this.dme.notRelatedComments = value;
        }
    }
});
